import { useCallback, useContext, useEffect, useMemo, useReducer, type ReactNode } from 'react'
import moment from 'moment'
import { connect } from 'react-redux'
import { useIntl } from 'react-intl'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useQueryParams from 'hooks/useQueryParams'
import { pushWithRegion } from 'actions/NavigationActions'
import type { FlightSearchWidgetState, FlightSearchWidgetDispatchAction } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import flightSearchWidgetStateReducer, { initialState, FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import FlightSearchWidgetStateProvider from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateProvider'
import { DISABLED_MULTI_CITY_REGION_CODES, FlightsFareTypes } from 'constants/flight'
import { isMultiTicketFlightsEnabled, isMultiTicketMultiCabinFlightsEnabled } from 'selectors/flightsMultiTicketSelectors'
import type { FlightsCreditDetails, FlightDropdownOption } from 'components/Flights/types'
import { isFlightsCredit } from 'selectors/flightsSelectors'
import FlightSearchWidgetDesktop from './FlightSearchWidgetDesktop'
import FlightSearchWidgetMobile from './FlightSearchWidgetMobile'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import ModalProvider from 'contexts/ModalProvider'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import type { LastSearchSessionData } from 'lib/flights/flightUtils'
import { buildFlightSearchNavigation, trackFlightSearch } from 'lib/flights/flightSearchSubmission'
import RecentFlightSearchesCarousel from 'components/Flights/RecentFlightSearchesCarousel/RecentFlightSearchesCarousel'
import uuidV4 from 'lib/string/uuidV4Utils'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import GeoContext from 'contexts/geoContext'
import { type RecentFlightSearchesData } from 'hooks/Flights/useRecentFlightSearches'
import type { Filter_1_0_0 } from '@luxuryescapes/contract-data-event-schemas/lib/com.luxgroup'
import styled from 'styled-components'
import { rem } from 'polished'
import noop from 'lib/function/noop'

export interface FlightSearchWidgetSearchData extends Partial<FlightSearchWidgetState> {
  filterAirlines?: Array<string>
  lastSearch?: LastSearchSessionData
  disableCalendarPricing?: boolean
  dealId?: string
}

interface Props {
  className?: string
  searchData?: FlightSearchWidgetSearchData
  creditDetails?: FlightsCreditDetails
  isFlightsCredit: boolean
  pathname: string
  /**
   * Sets the airports to a 'read only' mode
   * Used when defaulting airport selections such as on flight deal pages
   */
  readOnlyAirports?: boolean
  showMultiCity?: boolean
  recentSearchesData?: RecentFlightSearchesData
  filterContexts?: Array<Filter_1_0_0>
  autoFocusArrival?: boolean
  mobileResultsRedesign?: boolean
  mobileResultsCloseButton?: ReactNode
  onMobileResultsSearchComplete?: () => void
}

const MobileResultsSearchForm = styled(VerticalSpacer)`
  margin-inline: auto;
  max-width: ${rem(1000)};
  padding-block: 0 ${rem(32)};
  padding-inline: ${rem(20)};
`

export function getFlightSearchWidgetRecentStateData(
  lastSearch: LastSearchSessionData | undefined,
  fareTypes: Array<FlightDropdownOption>,
): Partial<FlightSearchWidgetState> {
  if (lastSearch?.fareType === FlightsFareTypes.MULTI_CITY) {
    return {}
  }

  const occupants = lastSearch?.occupants

  return {
    ...(lastSearch?.fareType && { fareType: fareTypes.find(f => f.value === lastSearch.fareType)! }),
    ...(occupants && { occupants }),
    ...((lastSearch?.flights?.length || 0) > 0 && {
      flights: lastSearch?.flights?.map(flight => {
        const checkinDate = flight?.departingDate ? moment(flight.departingDate) : undefined
        const checkoutDate = flight?.returningDate ? moment(flight.returningDate) : undefined
        const isValidDepartingDate = checkinDate?.isAfter(moment())

        return {
          id: uuidV4(),
          ...(checkinDate && isValidDepartingDate ? { checkinDate } : {}),
          ...(checkoutDate && isValidDepartingDate ? { checkoutDate } : {}),
          ...(flight?.departureAirport && { departureAirport: flight.departureAirport }),
          ...(flight?.arrivalAirport && { arrivalAirport: flight.arrivalAirport }),
        }
      }),
    }),
  }
}

function FlightSearchWidget(props: Props) {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const {
    searchData,
    isFlightsCredit,
    creditDetails,
    className,
    pathname,
    readOnlyAirports,
    showMultiCity,
    recentSearchesData,
    filterContexts,
    autoFocusArrival,
    mobileResultsRedesign,
    mobileResultsCloseButton,
    onMobileResultsSearchComplete = noop,
  } = props

  const { fareTypes, fareCabins } = useFlightFareLabels()

  const loadPrefilledData = !pathname.includes('flights-search-results')

  const query = useQueryParams()
  const departingQuotedPrice = query.get('departingQuotedPrice')
  const returningQuotedPrice = query.get('returningQuotedPrice')
  const source = query.get('source')
  const { currentRegionCode } = useContext(GeoContext)

  const recentStateData = loadPrefilledData ? getFlightSearchWidgetRecentStateData(searchData?.lastSearch, fareTypes) : {}
  // Preserve prefilled departure airports from searchData when merging with recent search data
  const hasPrefilledDeparture = searchData?.flights?.some(f => f.departureAirport)

  const [state, stateDispatch] = useReducer<FlightSearchWidgetState, [FlightSearchWidgetDispatchAction]>(flightSearchWidgetStateReducer, {
    ...initialState,
    fareType: fareTypes[0],
    fareCabin: fareCabins[0],
    ...searchData,
    ...recentStateData,
    // Re-apply searchData.flights if it has prefilled departure (to override recentStateData.flights)
    ...(hasPrefilledDeparture && searchData?.flights ? { flights: searchData.flights } : {}),
  })

  useEffect(() => {
    if (searchData) {
      const recentData = loadPrefilledData ? getFlightSearchWidgetRecentStateData(searchData?.lastSearch, fareTypes) : {}
      const hasPrefilledDep = searchData?.flights?.some(f => f.departureAirport)
      stateDispatch({
        type: FlightSearchWidgetActions.UPDATE_FLIGHT_SEARCH_WIDGET_DATA,
        payload: {
          ...state,
          ...searchData,
          ...recentData,
          // Re-apply searchData.flights if it has prefilled departure (to override recentData.flights)
          ...(hasPrefilledDep && searchData?.flights ? { flights: searchData.flights } : {}),
        },
      })
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [searchData, loadPrefilledData])

  const multiTicketEnabled = useAppSelector(isMultiTicketFlightsEnabled)
  const multiTicketMultiCabinEnabled = useAppSelector(isMultiTicketMultiCabinFlightsEnabled)

  const proceedWithSearch = useCallback(() => {
    trackFlightSearch(state, filterContexts)

    const { path, query, shouldResetDealId } = buildFlightSearchNavigation(state, {
      previousFlights: searchData?.flights,
      filterAirlines: searchData?.filterAirlines,
      dealId: searchData?.dealId,
      source,
      departingQuotedPrice,
      returningQuotedPrice,
      isFlightsCredit,
      creditCarrier: creditDetails?.carrier,
      multiTicket: multiTicketEnabled && state.multiTicket,
      multiTicketMultiCabinEnabled,
    })

    if (shouldResetDealId) {
      stateDispatch({ type: FlightSearchWidgetActions.RESET_DEAL_ID })
    }

    onMobileResultsSearchComplete()
    dispatch(pushWithRegion(path, query))
  }, [
    state,
    filterContexts,
    searchData,
    source,
    departingQuotedPrice,
    returningQuotedPrice,
    isFlightsCredit,
    creditDetails?.carrier,
    multiTicketEnabled,
    multiTicketMultiCabinEnabled,
    dispatch,
    onMobileResultsSearchComplete,
  ])

  const onSearchAirport = useCallback(async(e?: any) => {
    e?.preventDefault()
    proceedWithSearch()
  }, [proceedWithSearch])

  const isMultiCityRegionEnabled = useMemo(() => {
    return !DISABLED_MULTI_CITY_REGION_CODES.includes(currentRegionCode)
  }, [currentRegionCode])

  const multiCityEnabled = showMultiCity && isMultiCityRegionEnabled

  return (
    <FlightSearchWidgetStateProvider state={state} dispatch={stateDispatch}>
      <ModalProvider>
        <div className={className}>
          {!mobileResultsRedesign && <CSSBreakpoint min="tablet">
            <VerticalSpacer gap={16}>
              <FlightSearchWidgetDesktop
                onSearchAirport={onSearchAirport}
                readOnlyAirports={readOnlyAirports}
                showMultiCity={multiCityEnabled}
                autoFocusArrival={autoFocusArrival}
              />
              {recentSearchesData && recentSearchesData.shouldShowRecentSearches && (
                <RecentFlightSearchesCarousel
                  recentSearches={recentSearchesData.data}
                  status={recentSearchesData.status}
                />
              )}
            </VerticalSpacer>
          </CSSBreakpoint>}
          {!mobileResultsRedesign && <CSSBreakpoint max="mobile">
            <VerticalSpacer gap={12} as="form" onSubmit={onSearchAirport}>
              <FlightSearchWidgetMobile
                readOnlyAirports={readOnlyAirports}
                showMultiCity={multiCityEnabled}
                recentSearchesData={recentSearchesData}
              />
              <TextButton kind="primary" size="large" type="submit" fit="full-width">
                {intl.formatMessage({
                  id: '6D2lT3mm3F43mkH3nYrE',
                  defaultMessage: 'Search',
                  description: 'Search button text for flight search widget in the flights flow',
                })}
              </TextButton>
            </VerticalSpacer>
          </CSSBreakpoint>}
          {mobileResultsRedesign && <CSSBreakpoint max="tablet">
            <MobileResultsSearchForm gap={20} as="form" onSubmit={onSearchAirport}>
              <FlightSearchWidgetMobile
                readOnlyAirports={readOnlyAirports}
                showMultiCity={multiCityEnabled}
                mobileResultsRedesign
              />
              <VerticalSpacer gap={8}>
                <TextButton kind="primary" size="large" type="submit" fit="full-width">
                  {intl.formatMessage({
                    id: '6D2lT3mm3F43mkH3nYrE',
                    defaultMessage: 'Search',
                    description: 'Search button text for flight search widget in the flights flow',
                  })}
                </TextButton>
                {mobileResultsCloseButton}
              </VerticalSpacer>
            </MobileResultsSearchForm>
          </CSSBreakpoint>}
        </div>
      </ModalProvider>
    </FlightSearchWidgetStateProvider>
  )
}

function mapStateToProps(state: App.State) {
  return {
    creditDetails: state.flights.creditDetails,
    isFlightsCredit: isFlightsCredit(state),
    pathname: state.router.location.pathname,
  }
}

export default connect(mapStateToProps)(FlightSearchWidget)
